import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
} from '@angular/core';

import { ReportLevel } from '../report.model';
import { ChartTheme, levelColor, paletteFor } from './chart-palette';
import { clamp, round } from './chart-geometry';

/** Diamètre du cadran, en pixels — figé par la carte du résumé. */
const SIZE = 112;
/** Épaisseur de l'anneau. */
const STROKE = 9;

/**
 * Cadran circulaire du score, dans la carte de résumé.
 *
 * Le bloc central (note, /20, pourcentage) n'est pas dessiné ici : il est
 * projeté. Dans le document d'origine `.ring-meter__center` est un **frère** du
 * SVG, positionné en absolu sur `.ring-meter` — le SVG est pivoté de −90° par
 * la feuille de styles pour que l'arc démarre à midi, et un centre placé dedans
 * pivoterait avec lui. La structure frère/frère est donc conservée telle quelle.
 *
 * Hôte en `display: contents` : `.ring-meter` est une boîte flex de centrage,
 * l'hôte doit disparaître pour que le SVG en soit l'enfant direct.
 */
@Component({
  selector: 's2e-ring-meter',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.display]': "'contents'" },
  template: `
    <svg
      class="ring-meter__svg"
      [attr.width]="size"
      [attr.height]="size"
      [attr.viewBox]="viewBox"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        class="ring-meter__track"
        [attr.cx]="centre"
        [attr.cy]="centre"
        [attr.r]="radius"
        fill="none"
        [attr.stroke]="palette().track"
        [attr.stroke-width]="stroke"
      ></circle>
      <circle
        class="ring-meter__arc"
        data-meter-arc
        [attr.cx]="centre"
        [attr.cy]="centre"
        [attr.r]="radius"
        fill="none"
        [attr.stroke]="arcColor()"
        [attr.stroke-width]="stroke"
        stroke-linecap="round"
        [attr.stroke-dasharray]="circumference"
        [attr.stroke-dashoffset]="offset()"
        [style.--arc-length]="arcLength()"
        [style.--arc-offset]="arcOffset()"
      ></circle>
    </svg>
    <ng-content />
  `,
})
export class RingMeterComponent {
  /** Pourcentage global, borné : une valeur hors [0,100] enroulerait l'arc. */
  readonly percentage = input.required<number>();
  /** Palier atteint ; `null` quand le score ne tombe dans aucune tranche. */
  readonly level = input.required<ReportLevel | null>();
  readonly theme = input.required<ChartTheme>();

  readonly size = SIZE;
  readonly stroke = STROKE;
  readonly centre = SIZE / 2;
  readonly viewBox = `0 0 ${SIZE} ${SIZE}`;

  /** 51,5 px pour la géométrie de référence — le trait déborde de part et d'autre. */
  private readonly rawRadius = (SIZE - STROKE) / 2;
  readonly radius = round(this.rawRadius);

  private readonly rawCircumference = 2 * Math.PI * this.rawRadius;
  readonly circumference = round(this.rawCircumference);

  readonly palette = computed(() => paletteFor(this.theme()));
  readonly arcColor = computed(() => levelColor(this.level(), this.palette()));

  /** Longueur non tracée : l'arc se remplit en réduisant ce décalage. */
  readonly offset = computed(() =>
    round(this.rawCircumference * (1 - clamp(this.percentage(), 0, 100) / 100)),
  );

  /* L'animation `arc-draw` de la feuille de styles interpole `stroke-dashoffset`
     de `--arc-length` vers `--arc-offset` : sans ces deux propriétés
     personnalisées, l'arc apparaît d'un coup à sa valeur finale. Les attributs
     restent posés en parallèle, pour l'impression et pour `prefers-reduced-motion`,
     où la règle print force `stroke-dashoffset: var(--arc-offset)`. */
  readonly arcLength = computed(() => String(this.circumference));
  readonly arcOffset = computed(() => String(this.offset()));
}
